import React, { useState, useCallback } from 'react';
import copy from 'copy-to-clipboard';
import {
  IconCopyLine,
  IconCheckLine,
  IconButton,
  View,
} from '@smarterservices/smarter-ui';

/**
 * Props for the CopyValue component
 */
interface CopyValueProps {
  /** The text to be copied to the clipboard */
  text: string;
  /** Optional margin for the container */
  margin?: 'none' | 'small' | 'medium' | 'large' | 'x-small' | 'xx-small';
  /** Optional callback when copy is successful */
  onCopy?: (text: string) => void;
  /** Duration in milliseconds to show the success state (default: 1000) */
  successDuration?: number;
}

/**
 * CopyValue Component
 * 
 * Displays a copy button that allows users to copy text to their clipboard.
 * Shows a success checkmark briefly after copying.
 * 
 * @example
 * ```tsx
 * <CopyValue text="SID123456789" margin="small" />
 * ```
 */
const CopyValue: React.FC<CopyValueProps> = ({
  text,
  margin = 'none',
  onCopy,
  successDuration = 1000,
}) => {
  const [copied, setCopied] = useState(false);

  /**
   * Copies the provided text to the clipboard and temporarily sets the
   * copied state to true, indicating that the copy action was successful.
   * The copied state is reverted back to false after the specified duration.
   */
  const handleCopy = useCallback(() => {
    if (!text) {
      console.warn('CopyValue: No text provided to copy');
      return;
    }

    const success = copy(text);

    if (success) {
      setCopied(true);

      // Call the optional callback
      if (onCopy) {
        onCopy(text);
      }

      setTimeout(() => {
        setCopied(false);
      }, successDuration);
    } else {
      console.error('CopyValue: Failed to copy text to clipboard');
    }
  }, [text, onCopy, successDuration]);

  return (
    <View margin={margin} display="inline-block">
      {!copied ? (
        <IconButton
          onClick={handleCopy}
          size="small"
          themeOverride={{
            borderWidth: 0,
          }}
          screenReaderLabel={`Copy ${text}`}
        >
          <IconCopyLine />
        </IconButton>
      ) : (
        <IconButton
          size="small"
          themeOverride={{
            borderWidth: 0,
          }}
          screenReaderLabel="Value copied to clipboard"
        >
          <IconCheckLine color="success" />
        </IconButton>
      )}
    </View>
  );
};

export default CopyValue;
